// Operations Map — /app/operations-map
// Live operational control screen: jobs + crews + plant + trucks + safety in
// one map view. Pure orchestration over /api/operations/overview.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import "./operationsMap.css";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Map as MapIcon, Satellite, RefreshCw, Search, Loader2, Hammer, Truck, Car,
  Users, ShieldAlert, AlertTriangle, Info, Check, X, Clock, Wrench, MapPin,
  ExternalLink, CloudSun, Maximize2, ChevronRight, HardHat, Tractor,
} from "lucide-react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { gpsByAsset, gpsAge, locationLabel, assetGeo, validGeo } from "@/lib/operationsLocation";
import { useTodayWeather, WX_TEXT } from "@/lib/useWeather";

const AKL = [-36.8485, 174.7633];
const KIND_META = {
  plant: { label: "Plant", icon: Hammer, color: "#FF5F15" },
  truck: { label: "Truck", icon: Truck, color: "#38bdf8" },
  light: { label: "Light Vehicle", icon: Car, color: "#a1a1aa" },
};
const STATUS_META = {
  timesheet_active: { label: "Timesheet active", color: "#475569" },
  unassigned: { label: "Unassigned", color: "#71717a" },
  maintenance: { label: "Maintenance", color: "#71717a" },
  issue: { label: "Issue", color: "#ef4444" },
};
const SEV = {
  critical: { icon: ShieldAlert, cls: "text-red-800 dark:text-red-300 border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30" },
  warning: { icon: AlertTriangle, cls: "text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20" },
  info: { icon: Info, cls: "text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900" },
};

const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
// Render the shared icon set once; Leaflet div icons accept static HTML.
const MARKER_SYMBOLS = Object.fromEntries(
  Object.entries({ job: HardHat, plant: Tractor, truck: Truck, light: Car, crew: Users })
    .map(([kind, Icon]) => [kind, renderToStaticMarkup(<Icon size={22} strokeWidth={2.2} aria-hidden="true" />)])
);
const markerIcon = ({ kind, selected, label, count, unconfirmed, attention, anchor }) => L.divIcon({
  className: `ops-map-marker ops-map-marker--${kind}${selected ? ' is-selected' : ''}${unconfirmed ? ' is-unconfirmed' : ''}`,
  iconSize: [38, 38], iconAnchor: anchor || [19, 42], tooltipAnchor: [0, -42],
  html: `<div class="ops-marker-symbol">${MARKER_SYMBOLS[kind]}${count > 1 ? `<span class="ops-marker-count">${escapeHtml(count)}</span>` : ''}${attention ? '<span class="ops-marker-attention">!</span>' : ''}</div><span class="ops-marker-label">${escapeHtml(label)}</span>`,
});
const jobIcon = (job, selected) => markerIcon({
  kind: 'job', selected, label: job.job_number || job.title, attention: job.attention_count,
});
const assetIcon = (asset, selected) => {
  const loc = asset.gps || asset.map_location || asset.location;
  const age = gpsAge(asset.gps);
  const recent = asset.gps && age != null && age <= 15;
  const label = asset.gps ? (recent ? 'GPS' : 'LAST GPS') : loc?.source === 'allocated_job' ? 'PLAN' : 'TS JOB';
  const kind = ['plant', 'truck', 'light'].includes(asset.kind) ? asset.kind : 'plant';
  // Keep each resource type visible when it shares a job reference coordinate.
  const anchor = asset.gps ? [19, 19] : { plant: [66, -20], truck: [19, -20], light: [-28, -20] }[kind];
  return markerIcon({ kind, selected, label, count: asset.groupCount, unconfirmed: !recent, anchor });
};
const crewIcon = c => markerIcon({ kind: 'crew', label: 'JOB', count: c.count, unconfirmed: true, anchor: [-28, 42] });

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  return isNaN(d) ? iso : d.toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
};

function MapController({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target?.type === "fit" && target.bounds?.length) {
      map.fitBounds(target.bounds, { padding: [50, 50], maxZoom: 15 });
    } else if (target?.pos) {
      map.flyTo(target.pos, Math.max(map.getZoom(), 15), { duration: 0.6 });
    }
  }, [target, map]);
  useEffect(() => {
    const observer = new ResizeObserver(() => map.invalidateSize({pan:false}));
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);
  return null;
}

const Row = ({ label, value }) => value !== null && value !== undefined && value !== "" ? (
  <div className="flex justify-between gap-3 py-1.5 border-b border-white/[0.05] text-[12.5px]">
    <span className="text-zinc-500">{label}</span><span className="font-semibold text-right">{value}</span>
  </div>
) : null;

export default function OperationsMap() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [tab, setTab] = useState("jobs");
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState(() => (localStorage.getItem("fl_ops_kind") === "crews" ? "people" : localStorage.getItem("fl_ops_kind")) || "all");
  const [satellite, setSatellite] = useState(() => localStorage.getItem("fl_ops_style") !== "map");
  const [sel, setSel] = useState(null); // {type:'asset'|'job'|'crew', id}
  const [mobileView, setMobileView] = useState("map");
  const [error, setError] = useState("");
  const [gpsData, setGpsData] = useState(null);
  const [gpsState, setGpsState] = useState("loading");
  const [clock, setClock] = useState(Date.now());
  const [target, setTarget] = useState(null);
  const wx = useTodayWeather();
  const timer = useRef(null);
  const focusedAsset = params.get("asset");
  const focusedJob = params.get("job");
  const appliedFocus = useRef(null);
  const loadRequest = useRef(null);

  const load = useCallback(async (quiet) => {
    loadRequest.current?.abort();
    const controller = new AbortController(); loadRequest.current = controller;
    if (!quiet) setLoading(true);
    try {
      const { data: d } = await api.get("/operations/overview", {params:{asset_id:focusedAsset || undefined},signal:controller.signal});
      if(controller.signal.aborted) return;
      setData(d); setError("");
      setUpdatedAt(Date.now()); setClock(Date.now());
    } catch (e) { if (!controller.signal.aborted) setError(e.response?.data?.detail || "Could not refresh operations. Previous data remains visible."); } finally { if(!controller.signal.aborted) setLoading(false); }
  }, [focusedAsset]);
  useEffect(() => {
    load();
    timer.current = setInterval(() => load(true), 60000);
    return () => {clearInterval(timer.current);loadRequest.current?.abort();};
  }, [load]);

  useEffect(() => {
    const controller = new AbortController();
    const loadGps = async () => {
      try {
        const {data: result} = await api.get('/tracking/vehicles/status', {signal:controller.signal});
        if (!controller.signal.aborted) { setGpsData(result); setGpsState('connected'); }
      } catch (e) {
        if (!controller.signal.aborted) { setGpsData(null); setGpsState(e.response?.status === 400 ? 'disconnected' : 'unavailable'); }
      }
    };
    loadGps(); const poll = setInterval(loadGps, 60000);
    const tick = setInterval(() => setClock(Date.now()), 10000);
    return () => { controller.abort(); clearInterval(poll); clearInterval(tick); };
  }, []);
  useEffect(() => { localStorage.setItem("fl_ops_kind", kindFilter); }, [kindFilter]);
  useEffect(() => { localStorage.setItem("fl_ops_style", satellite ? "sat" : "map"); }, [satellite]);

  const jobs = data?.jobs || [];
  const assets = useMemo(() => gpsByAsset(data?.assets || [], gpsData), [data, gpsData]);
  const people = data?.people || [];
  const crews = data?.crews || [];
  const alerts = data?.alerts || [];
  const jobsById = useMemo(() => Object.fromEntries(jobs.map((j) => [j.id, j])), [jobs]);

  // Focus after the requested asset arrives; do not recenter every poll.
  useEffect(() => {
    if (!data) return;
    const key = `${focusedJob || ''}|${focusedAsset || ''}`;
    if (appliedFocus.current === key) return;
    if (focusedJob && jobsById[focusedJob]) select({type:'job',id:focusedJob},false);
    else if (focusedAsset && assets.find(a=>a.id===focusedAsset)) select({type:'asset',id:focusedAsset},false);
    else if (focusedJob || focusedAsset) return;
    appliedFocus.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, focusedJob, focusedAsset]);

  const select = (s, updateUrl = true) => {
    setSel(s);
    if (updateUrl) {
      const p = new URLSearchParams();
      if (s && ["job", "asset"].includes(s.type)) p.set(s.type, s.id);
      appliedFocus.current = `${p.get("job") || ""}|${p.get("asset") || ""}`;
      setParams(p, { replace: true });
    }
    if (!s) return;
    if (s.type === "job") {
      const g = jobsById[s.id]?.geo;
      if (g) setTarget({ pos: [g.lat, g.lng], t: Date.now() });
    } else if (s.type === "asset" || s.type === "crew" || s.type === "person") {
      const a = s.type === "asset" ? assets.find((x) => x.id === s.id) : null;
      const person = people.find(p => p.id === s.id);
      const jid = s.type === "crew" ? s.id : s.type === "person" ? (person?.timesheets[0]?.job_id || person?.allocations[0]?.job_id) : a?.job_id;
      const g = (a ? assetGeo(a) : null) || jobsById[jid]?.geo;
      if (g) setTarget({ pos: [g.lat, g.lng], t: Date.now() });
    }
  };

  const fitAll = () => {
    const pts = [...jobs.map(j => j.geo), ...assets.map(assetGeo)].filter(validGeo).map(g => [g.lat,g.lng]);
    if (pts.length) setTarget({ type: "fit", bounds: pts, t: Date.now() });
  };
  useEffect(() => { if (data && !params.get("job") && !params.get("asset")) fitAll(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data === null]);

  const ql = q.trim().toLowerCase();
  const match = (s) => !ql || (s || "").toLowerCase().includes(ql);
  const filteredAssets = assets.filter((a) =>
    (kindFilter === "all" || (!["people", "jobs"].includes(kindFilter) && a.kind === kindFilter)) &&
    (match(a.name) || match(a.rego) || match(a.operator) || match(a.job_number) || match(a.job_title)));
  const filteredJobs = jobs.filter((j) => match(j.job_number) || match(j.title) || match(j.client_name));
  const showAssets = kindFilter === "all" || ["plant", "truck", "light"].includes(kindFilter);
  const showJobs = kindFilter === "all" || kindFilter === "jobs";
  const showCrews = kindFilter === "all" || kindFilter === "people";

  const selAsset = sel?.type === "asset" ? assets.find((a) => a.id === sel.id) : null;
  const selJob = sel?.type === "job" ? jobsById[sel.id] : null;
  const selPerson = sel?.type === "person" ? people.find(p => p.id === sel.id) : null;
  const selCrew = sel?.type === "crew" ? crews.find((c) => c.job_id === sel.id) : null;

  const ack = async (key) => {
    try { await api.post("/operations/alerts/ack", { key }); load(true); } catch { /* noop */ }
  };

  const events = data?.events || [];
  const eventsFor = (pred) => events.filter(pred).slice(0, 12);
  const ago = updatedAt ? Math.round((clock - updatedAt) / 1000) : null;

  const listAssets = assets.filter(a => (tab === 'plant' ? a.kind === 'plant' : ['truck','light'].includes(a.kind)) && [a.name,a.rego,a.job_number,...(a.planned_today || []).map(b=>b.job_number)].some(match));
  const listPeople = people.filter(p => match(p.name) || [...p.allocations,...p.timesheets].some(r=>match(jobsById[r.job_id]?.job_number) || match(r.job_title)));
  const mapAssets = useMemo(() => {
    const grouped = new Map();
    filteredAssets.forEach(a => {
      const g = assetGeo(a); if (!validGeo(g)) return;
      const key = a.gps ? a.id : `${g.lat}:${g.lng}:${a.kind}:${(a.map_location || a.location)?.source}`;
      const old = grouped.get(key);
      grouped.set(key, old ? {...old,groupCount:(old.groupCount || 1)+1} : a);
    });
    return [...grouped.values()];
  }, [assets, kindFilter, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const peopleAtJobs = jobs.map(j => ({job_id:j.id,count:j.people_ids?.length || 0})).filter(c=>c.count);


  return (
    <div className="flex flex-col h-[calc(100vh-150px)] min-h-[600px] -mx-4 md:mx-0" data-testid="operations-map-page">
      {/* header */}
      <div className="flex items-center gap-3 flex-wrap px-4 md:px-0 pb-3">
        <div className="mr-2">
          <h1 className="font-display font-black text-xl tracking-tight">Operations</h1>
          <p className="text-[12px] text-zinc-500">Live operational view of today's jobs, people, plant and vehicles.</p>
        </div>
        <div className="flex gap-1 flex-wrap" role="group" aria-label="Marker filters">
          {[["all", "All"], ["jobs", "Jobs"], ["people", "People"], ["plant", "Plant"], ["truck", "Trucks"], ["light", "Light Vehicles"]].map(([k, l]) => (
            <button key={k} onClick={() => setKindFilter(k)} data-testid={`ops-filter-${k}`}
              className={`h-8 px-3 rounded-md text-[11.5px] font-bold border transition-colors duration-150 ${kindFilter === k ? "border-[#FF5F15] text-[#FF5F15] bg-[#FF5F15]/10" : "border-zinc-300 dark:border-white/10 text-zinc-500 hover:text-zinc-200"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        {wx?.temp != null && (
          <span className="hidden md:flex items-center gap-1.5 text-[12px] text-zinc-400" data-testid="ops-weather">
            <CloudSun className="w-4 h-4 text-[#FF5F15]" /> {Math.round(wx.temp)}°C {WX_TEXT(wx.code)}
          </span>
        )}
        <span className="text-[11px] font-mono text-zinc-500" data-testid="ops-updated">
          {ago == null ? "—" : ago < 60 ? `Updated ${ago}s ago` : `Updated ${Math.round(ago / 60)}m ago`}
        </span>
        <button onClick={() => load(true)} className="w-8 h-8 flex items-center justify-center rounded-md border border-zinc-300 dark:border-white/10 text-zinc-500 hover:text-[#FF5F15]" data-testid="ops-refresh" aria-label="Refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {error && <p role="alert" className="text-xs text-red-700 px-3 pb-2">{error}</p>}
      {data?.partial && <p className="text-xs text-amber-800 pb-2">Map capacity reached. Job/fleet lists and totals may be incomplete.</p>}
      <div className="flex gap-2 pb-2 px-4 lg:hidden">{['map','list'].map(view=><button key={view} onClick={()=>setMobileView(view)} aria-pressed={mobileView===view} className={`border rounded px-4 py-1 text-xs ${mobileView===view ? 'border-[#FF5F15] text-[#FF5F15]' : ''}`}>{view==='map' ? 'Map' : 'List'}</button>)}</div>
      {/* body */}
      <div className="flex-1 min-h-0 flex relative border border-zinc-300 dark:border-white/10 rounded-lg overflow-hidden bg-white dark:bg-[#0B0F14]">
        {/* Existing list, regrouped by operational evidence; independent of map filters. */}
        <div className={`${mobileView === 'list' ? 'flex' : 'hidden'} lg:flex w-full lg:w-[320px] shrink-0 flex-col border-r border-zinc-200 dark:border-white/10`}>
          <div className="flex border-b border-zinc-200 dark:border-white/10" role="tablist" aria-label="Operational context">
            {['jobs','people','plant','trucks','attention'].map(k=><button key={k} role="tab" aria-selected={tab===k} onClick={()=>setTab(k)} data-testid={`ops-tab-${k}`} className={`flex-1 py-3 text-[11px] font-bold capitalize border-b-2 ${tab===k ? 'border-[#FF5F15] text-[#FF5F15]' : 'border-transparent text-zinc-500'}`}>{k}</button>)}
          </div>
          <div className="p-2.5 border-b border-zinc-200 dark:border-white/10 relative"><Search className="absolute left-5 top-5 w-3.5 h-3.5 text-zinc-500"/><input aria-label="Search operations" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search jobs, people, plant..." className="w-full h-9 pl-8 pr-2 rounded border bg-zinc-50 dark:bg-zinc-900 text-xs" data-testid="ops-search"/></div>
          <div className="flex-1 overflow-y-auto">
            {tab==='jobs' && <>
              {filteredJobs.map(j=><button key={j.id} onClick={()=>select({type:'job',id:j.id})} data-testid={`ops-job-${j.id}`} className={`w-full px-3 py-3 text-left border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900 ${sel?.id===j.id ? 'border-l-2 border-l-[#FF5F15]' : ''}`}>
                <span className="block text-[11px] font-mono text-[#FF5F15]">{j.job_number} · {(j.status || '').replaceAll('_',' ')}</span><strong className="block text-sm truncate">{j.title}</strong>
                <span className="block text-xs text-zinc-500 mt-1">{j.scheduled_count || 0} scheduled · {j.active_timesheets || 0} active timesheets<br/>{j.plant_allocated || 0} plant allocated{j.attention_count ? ` · ${j.attention_count} attention` : ''}</span>
                {!j.geo && <span className="text-[10px] text-zinc-500">Job location not recorded</span>}
              </button>)}
              {!filteredJobs.length && <p className="p-4 text-xs text-zinc-500">No current Jobs match this search.</p>}
            </>}
            {tab==='people' && <>{listPeople.map(p=><button key={p.id} onClick={()=>select({type:'person',id:p.id})} className="w-full text-left p-3 border-b border-zinc-100 dark:border-zinc-800">
              <strong className="block text-sm">{p.name}</strong><span className="block text-xs text-zinc-500">{[...new Set([...p.allocations,...p.timesheets].map(r=>jobsById[r.job_id]?.job_number || r.job_title).filter(Boolean))].join(' · ')}</span><span className="text-[11px] font-semibold">{p.evidence}</span>
            </button>)}{!listPeople.length && <p className="p-4 text-xs text-zinc-500">No crew allocations or work timesheets match today.</p>}</>}
            {['plant','trucks'].includes(tab) && <>{listAssets.map(a=><button key={a.id} onClick={()=>select({type:'asset',id:a.id})} data-testid={`ops-asset-${a.id}`} className="w-full text-left p-3 border-b border-zinc-100 dark:border-zinc-800">
              <strong className="block text-sm">{a.name}</strong><span className="block text-xs text-zinc-500">{a.rego} · {a.job_number || (a.planned_today || []).map(b=>b.job_number).join(', ') || 'No Job allocation today'}</span><span className="block text-[11px]">{a.evidence} · {locationLabel(a,clock)}</span>
            </button>)}{!listAssets.length && <p className="p-4 text-xs text-zinc-500">No matching resources.</p>}</>}
            {tab==='attention' && <div className="p-2 space-y-2">{alerts.filter(al=>match(al.title)||match(al.detail)).map(al=><div key={al.key} className={`border rounded p-3 ${(SEV[al.severity] || SEV.info).cls}`}>
              <strong className="text-xs">{al.title}</strong><p className="text-xs mt-1">{al.detail}</p><div className="flex justify-between mt-2 text-xs"><button onClick={()=>select(al.asset_id ? {type:'asset',id:al.asset_id} : al.person_id ? {type:'person',id:al.person_id} : {type:'job',id:al.job_id})}>View context</button><button onClick={()=>ack(al.key)}>Acknowledge</button></div>
            </div>)}{!alerts.length && <p className="p-3 text-xs text-zinc-500">No operational items need attention.</p>}</div>}
          </div>
          <div className="border-t p-3 text-[10px] text-zinc-500">Planning is not physical presence. Recorded work and GPS are separate evidence.<br/>{data?.kpis.trucks_today || 0} truck sheets recorded today.</div>
        </div>

        {/* MAP */}
        <div className={`${mobileView === "map" ? "block" : "hidden"} lg:block flex-1 min-w-0 relative`}>
          {loading && !data ? (
            <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#FF5F15]" /></div>
          ) : (
            <MapContainer center={AKL} zoom={11} className="w-full h-full" zoomControl={true} attributionControl={true}>
              {satellite ? (
                <TileLayer attribution="Tiles &copy; Esri" url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
              ) : (
                <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" referrerPolicy="strict-origin-when-cross-origin" />
              )}
              <MapController target={target} />
              {showJobs !== false && filteredJobs.filter((j) => validGeo(j.geo)).map((j) => (
                <Marker key={j.id} position={[j.geo.lat, j.geo.lng]} icon={jobIcon(j, sel?.type === "job" && sel.id === j.id)}
                  title={`${j.job_number || j.title} — Job`}
                  eventHandlers={{ click: () => select({ type: "job", id: j.id }) }}>
                  <Tooltip><strong>{j.job_number} · {j.title}</strong><br />{j.scheduled_count || 0} scheduled · {j.plant_allocated || 0} plant{j.attention_count ? ` · ${j.attention_count} attention` : ''}</Tooltip>
                </Marker>
              ))}
              {showAssets && mapAssets.map(a => {
                const g = assetGeo(a);
                return <Marker key={a.id} position={[g.lat,g.lng]} title={`${a.name} — ${locationLabel(a,clock)}`} icon={assetIcon(a,sel?.id===a.id)} eventHandlers={{click:()=>select(a.groupCount > 1 && (a.map_location || a.location)?.job?.id ? {type:'job',id:(a.map_location || a.location).job.id} : {type:'asset',id:a.id})}}><Tooltip>{a.groupCount > 1 ? `${a.groupCount} resources at this Job reference` : a.name}<br/>{locationLabel(a,clock)}</Tooltip></Marker>;
              })}
              {showCrews && peopleAtJobs.map(c => {
                const g=jobsById[c.job_id]?.geo; if (!validGeo(g)) return null;
                return <Marker key={c.job_id} position={[g.lat,g.lng]} title={`${c.count} people at Job reference`} icon={crewIcon(c)} eventHandlers={{click:()=>select({type:'job',id:c.job_id})}}><Tooltip>People associated with this Job by planning or timesheets. Not GPS.</Tooltip></Marker>;
              })}
            </MapContainer>
          )}
          {/* map controls */}
          <div className="absolute top-3 right-3 z-[1000] flex flex-col gap-1.5">
            <button onClick={() => setSatellite((s) => !s)} data-testid="ops-style-toggle" aria-label="Toggle satellite"
              className="h-9 px-3 flex items-center gap-1.5 rounded-md bg-white border border-zinc-200 text-[11px] font-bold text-zinc-700 hover:text-[#FF5F15]">
              {satellite ? <MapIcon className="w-4 h-4" /> : <Satellite className="w-4 h-4" />} {satellite ? "Map" : "Satellite"}
            </button>
            <button onClick={fitAll} data-testid="ops-fit-all" aria-label="Fit all"
              className="h-9 px-3 flex items-center gap-1.5 rounded-md bg-white border border-zinc-200 text-[11px] font-bold text-zinc-700 hover:text-[#FF5F15]">
              <Maximize2 className="w-4 h-4" /> Fit all
            </button>
          </div>
          <div className="absolute bottom-6 left-3 z-[1000] rounded bg-white border border-zinc-200 px-3 py-2 text-[10px] text-zinc-600 max-w-[240px]" data-testid="ops-legend">
            <div className="grid grid-cols-3 gap-x-3 gap-y-2 mb-2 font-semibold">
              {[[HardHat, 'Jobs', '#c2410c'], [Tractor, 'Plant', '#a16207'], [Truck, 'Trucks', '#0369a1'], [Car, 'Vehicles', '#0f766e'], [Users, 'People', '#7c3aed']].map(([Icon, label, color]) => <span key={label} className="flex items-center gap-1"><Icon size={15} style={{ color }} />{label}</span>)}
            </div><p>GPS = provider position · dashed = older/unconfirmed</p><p>TS JOB / PLAN / people = Job reference, not GPS</p>
          </div>
          <div className="absolute top-14 right-3 z-[1000] max-w-[240px] bg-white border rounded p-2 text-[10px] text-zinc-600">
            {gpsState==='disconnected' ? 'GPS tracking is not connected. Showing planned and recorded operational locations.' : gpsState==='unavailable' ? 'GPS status unavailable. Showing planned and recorded Job locations.' : gpsState==='loading' ? 'Checking GPS connection…' : 'GPS · Cartrack. Reports older than 15 minutes are muted; timestamps do not confirm machine activity.'}
          </div>
        </div>

        {/* RIGHT context panel */}
        <Sheet open={!!(selAsset || selJob || selCrew || selPerson)} onOpenChange={open=>{if(!open) select(null);}} modal={false}>
          <SheetContent className="z-[1200] w-full sm:max-w-[460px] px-0" onInteractOutside={event=>event.preventDefault()} data-testid="ops-detail-panel">
            <SheetTitle className="sr-only">Operations details</SheetTitle><SheetDescription className="sr-only">Recorded evidence and planned allocations; not confirmation of physical presence.</SheetDescription>
            <div className="flex items-start justify-between px-4 pt-4">
              <div>
                {selAsset && (<>
                  <h2 className="font-display font-black uppercase tracking-tight text-lg">{selAsset.name}</h2>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold mt-0.5" style={{ color: (STATUS_META[selAsset.status] || {}).color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: (STATUS_META[selAsset.status] || {}).color }} /> {(STATUS_META[selAsset.status] || {}).label}
                  </span>
                </>)}
                {selJob && (<>
                  <div className="font-mono text-[11px] font-bold text-[#FF5F15]">{selJob.job_number}</div>
                  <h2 className="font-display font-black uppercase tracking-tight text-lg leading-tight">{selJob.title}</h2>
                </>)}
                {selCrew && (<>
                  <h2 className="font-display font-black uppercase tracking-tight text-lg">Crew — {selCrew.job_number}</h2>
                  <span className="text-[11px] text-zinc-500">{selCrew.job_title}</span>
                </>)}
              </div>

            </div>

            <div className="px-4 pb-4 mt-2">
              {selPerson && <>
                <h2 className="text-lg font-bold">{selPerson.name}</h2><p className="text-xs my-2">{selPerson.evidence} · No personal GPS</p>
                <h3 className="text-xs font-bold mt-4">PLANNER · TODAY</h3>
                {selPerson.allocations.map((a,i)=><Row key={i} label={jobsById[a.job_id]?.job_number || a.job_title || 'Job'} value={`Scheduled${a.start_time ? ` · ${a.start_time}` : ''}`} />)}
                {!selPerson.allocations.length && <p className="text-xs text-zinc-500 mt-2">No crew allocations for today.</p>}
                <h3 className="text-xs font-bold mt-4">TIMESHEETS</h3>
                {selPerson.timesheets.map((t,i)=><Row key={i} label={jobsById[t.job_id]?.job_number || t.job_title || 'Work record'} value={`${t.active ? 'Active since' : 'Recorded'} ${t.start_time || ''}${t.end_time ? ` – ${t.end_time}` : ''}`} />)}
                {!selPerson.timesheets.length && <p className="text-xs text-zinc-500 mt-2">No work timesheet recorded today.</p>}
                <Link to="/app/timesheets" className="inline-block mt-5 text-sm text-[#FF5F15]">Open Timesheets</Link>
              </>}

              {selAsset && (<>
                <Row label="Location source" value={locationLabel(selAsset,clock)} />
                {selAsset.gps && <><Row label="Provider" value={selAsset.gps.provider}/><Row label="Provider timestamp" value={selAsset.gps.recorded_at || 'Not supplied'}/></>}
                <Row label="Evidence" value={selAsset.evidence}/>
                <h3 className="text-xs font-bold mt-4">PLANNED · TODAY</h3>
                {(selAsset.planned_today || []).map((b,i)=><Row key={i} label={b.job_number || 'Job'} value={b.job_title}/>)}
                {!selAsset.planned_today?.length && <p className="text-xs text-zinc-500 mt-2">No allocation recorded today.</p>}
                <h3 className="text-xs font-bold mt-4">PLANNED · TOMORROW</h3>
                {(selAsset.planned_tomorrow || []).map((b,i)=><Row key={i} label={b.job_number || 'Job'} value={b.job_title}/>)}
                {!selAsset.planned_tomorrow?.length && <p className="text-xs text-zinc-500 mt-2">No allocation recorded for tomorrow.</p>}
                {selAsset.planning_signal && <p className="text-xs mt-3"><strong>{selAsset.planning_signal.label}</strong><br/>{selAsset.planning_signal.basis}</p>}
                <h3 className="text-xs font-bold mt-4">FIELD RECORDS</h3>
                {!selAsset.gps && <Row label="Physical location" value="Not GPS-confirmed"/>}
                <Row label="Operator" value={selAsset.operator || "Not on a timesheet today"} />
                <Row label="Recorded Job today" value={selAsset.job_number ? `${selAsset.job_number} — ${selAsset.job_title}` : null} />
                {!selAsset.job_number && selAsset.last_seen && (
                  <Row label="Last recorded Job" value={`${selAsset.last_seen.job_number || ""} ${selAsset.last_seen.job_title || ""}`.trim() || "—"} />
                )}
                {!selAsset.job_number && selAsset.last_seen && (
                  <Row label="Recorded date" value={`${fmtDate(selAsset.last_seen.date)}${selAsset.last_seen.operator ? " · " + selAsset.last_seen.operator : ""}`} />
                )}
                {!selAsset.job_number && selAsset.last_seen && !selAsset.last_geo && (
                  <p className="mt-2 text-[11.5px] text-amber-400 flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Last job has no map location — add a site address on that job to place this asset.</p>
                )}
                <Row label="Start time" value={selAsset.start_time} />
                <Row label="Hours today" value={selAsset.hours_today ? `${selAsset.hours_today} hrs` : null} />
                <Row label="Rego" value={selAsset.rego} />
                <Row label="Pre-start today" value={selAsset.prestart_done ? (selAsset.prestart_safe === true ? "Passed" : selAsset.prestart_safe === false ? "Failed" : "Recorded") : "Not recorded"} />
                <Row label="WoF expiry" value={selAsset.wof_expiry} />
                <Row label="Service due" value={selAsset.service_due} />
                <div className="mt-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2">Today's activity</div>
                  {eventsFor((e) => e.asset_ids?.includes(selAsset.id)).map((e, i) => (
                    <div key={i} className="flex gap-2.5 py-1.5 text-[12px]">
                      <span className="font-mono text-zinc-500 shrink-0">{e.time}</span>
                      {e.kind === "prestart" ? <Wrench className="w-3.5 h-3.5 text-[#FF5F15] shrink-0 mt-0.5" /> : <Clock className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />}
                      <span className="text-zinc-700 dark:text-zinc-300">{e.text}</span>
                    </div>
                  ))}
                  {!eventsFor((e) => e.asset_ids?.includes(selAsset.id)).length && <p className="text-[12px] text-zinc-500">No activity recorded today.</p>}
                </div>
                <div className="flex gap-2 mt-5">
                  <Link to="/app/fleet" className="flex-1 h-10 rounded-md border border-zinc-300 dark:border-white/15 flex items-center justify-center text-[11.5px] font-bold uppercase tracking-wider hover:border-[#FF5F15] hover:text-[#FF5F15]" data-testid="ops-view-asset">View Asset</Link>
                  {selAsset.job_id && <button onClick={() => nav(`/app/jobs/${selAsset.job_id}`)} className="flex-1 h-10 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white flex items-center justify-center text-[11.5px] font-bold uppercase tracking-wider" data-testid="ops-view-job">View Job</button>}
                </div>
              </>)}

              {selJob && (<>
                <Row label="Status" value={(selJob.status || "").replace("_", " ")} />
                <Row label="Client" value={selJob.client_name} />
                <Row label="Location" value={selJob.site_address || "No site address on the job"} />
                <Row label="People scheduled" value={selJob.scheduled_count || 0} />
                <Row label="Active timesheets" value={selJob.active_timesheets || 0} />
                <Row label="Plant allocated" value={selJob.plant_allocated || 0} />
                <Row label="Open Crew Needs" value={selJob.open_crew_needs || 0} />
                <h3 className="text-xs font-bold mt-4 mb-2">PEOPLE · TODAY</h3>
                {people.filter(p=>selJob.people_ids?.includes(p.id)).map(p=>{const records=p.timesheets.filter(t=>t.job_id===selJob.id);return <button key={p.id} onClick={()=>select({type:'person',id:p.id})} className="block w-full text-left py-2 border-b text-xs"><strong>{p.name}</strong><br/>{records.some(t=>t.active) ? 'Timesheet active' : records.length ? 'Timesheet recorded' : 'Scheduled'}{records[0]?.start_time ? ` · ${records[0].start_time}` : ''}</button>;})}
                {!selJob.people_ids?.length && <p className="text-xs text-zinc-500">No allocations or work timesheets recorded today.</p>}
                <h3 className="text-xs font-bold mt-4 mb-2">PLANT / VEHICLES</h3>
                {assets.filter(a=>selJob.allocated_asset_ids?.includes(a.id) || a.location?.current && a.location?.job?.id===selJob.id).map(a=><button key={a.id} onClick={()=>select({type:'asset',id:a.id})} className="block w-full text-left py-2 border-b text-xs"><strong>{a.name}</strong><br/>{locationLabel(a,clock)} · {a.evidence}</button>)}
                <h3 className="text-xs font-bold mt-4 mb-2">TODAY'S WORK</h3><p className="text-xs whitespace-pre-wrap">{selJob.instruction || 'No shared Crew Planner instruction recorded for today.'}</p>
                <Row label="Dispatched today" value={selJob.dispatched?.length ? selJob.dispatched.join(", ") : "Nobody yet"} />
                {!selJob.geo && (
                  <p className="mt-3 text-[11.5px] text-amber-400 flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Location data isn't available for this job yet — add a site address on the job to place it on the map.</p>
                )}
                <div className="mt-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2">Today's activity</div>
                  {eventsFor((e) => e.job_id === selJob.id).map((e, i) => (
                    <div key={i} className="flex gap-2.5 py-1.5 text-[12px]">
                      <span className="font-mono text-zinc-500 shrink-0">{e.time}</span>
                      <Clock className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
                      <span className="text-zinc-700 dark:text-zinc-300">{e.text}</span>
                    </div>
                  ))}
                  {!eventsFor((e) => e.job_id === selJob.id).length && <p className="text-[12px] text-zinc-500">No activity recorded today.</p>}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-5">
                  <button onClick={() => nav(`/app/jobs/${selJob.id}`)} className="h-10 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11.5px] font-bold uppercase tracking-wider" data-testid="ops-open-job">Open Job</button>
                  <button onClick={() => nav("/app/crew-planner?view=day")} className="h-10 rounded-md border border-zinc-300 dark:border-white/15 text-[11.5px] font-bold uppercase tracking-wider hover:border-[#FF5F15] hover:text-[#FF5F15]">Crew Planner</button>
                </div>
              </>)}

              {selCrew && (<>
                <Row label="Active timesheets" value={`${selCrew.count} people`} />
                <Row label="Since" value={selCrew.earliest_start} />
                <div className="mt-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2">Workers</div>
                  {selCrew.workers.map((w) => (
                    <div key={w} className="flex items-center gap-2 py-1 text-[12.5px]"><Users className="w-3.5 h-3.5 text-[#a78bfa]" /> {w}</div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-5">
                  <button onClick={() => nav(`/app/jobs/${selCrew.job_id}`)} className="h-10 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11.5px] font-bold uppercase tracking-wider">Open Job</button>
                  <button onClick={() => nav("/app/timesheets")} className="h-10 rounded-md border border-zinc-300 dark:border-white/15 text-[11.5px] font-bold uppercase tracking-wider hover:border-[#FF5F15] hover:text-[#FF5F15]">Timesheets</button>
                </div>
              </>)}
            </div>
            <div className="px-4 pb-4"><h3 className="text-xs font-bold mb-2">ATTENTION</h3>{alerts.filter(a=>selAsset ? a.asset_id===selAsset.id : selPerson ? a.person_id===selPerson.id : a.job_id===selJob?.id || selJob?.allocated_asset_ids?.includes(a.asset_id)).map(a=><p key={a.key} className="text-xs py-2 border-b"><strong>{a.title}</strong><br/>{a.detail}</p>)}</div>
          </SheetContent>
        </Sheet>
      </div>

      {/* KPI strip */}
      {data && (
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 pt-3 px-4 md:px-0" data-testid="ops-kpis">
          {[
            ["Active jobs", data.kpis.active_jobs, `${data.kpis.sites_with_geo} on the map`],
            ["People scheduled", data.kpis.people_scheduled, "Crew Planner · today"],
            ["Active timesheets", data.kpis.active_timesheets, "start recorded · no end"],
            ["Plant allocated", data.kpis.plant_allocated, "Crew Planner · today"],
            ["Pre-starts", data.kpis.prestarts, "recorded today"],
            ["Needs attention", data.kpis.alerts, "recorded exceptions"],
          ].map(([l, v, s]) => (
            <div key={l} className="rounded-md border border-zinc-300 dark:border-white/10 bg-white dark:bg-white/[0.02] px-3 py-2">
              <div className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-zinc-500">{l}</div>
              <div className="font-display font-black text-xl text-[#FF5F15] leading-tight">{v}</div>
              <div className="text-[10.5px] text-zinc-500">{s}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
